import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { mockTravelers, mockCampaigns } from '../../data/mockData';
import TravelerCard from './TravelerCard';
import TravelerForm from './TravelerForm';
import TravelerDetails from './TravelerDetails';
import { FaPlus, FaSearch, FaFilter, FaUsers } from 'react-icons/fa';

const TravelerManagement = () => {
  const { t } = useTranslation('travelers');
  const [travelers, setTravelers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [filteredTravelers, setFilteredTravelers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTraveler, setSelectedTraveler] = useState(null);
  const [editingTraveler, setEditingTraveler] = useState(null);

  useEffect(() => {
    // Load mock data
    setTravelers(mockTravelers.filter(t => !t.isDeleted));
    setCampaigns(mockCampaigns.filter(c => !c.isDeleted));
  }, []);

  useEffect(() => {
    // Apply filters
    let filtered = travelers;

    if (searchTerm) {
      filtered = filtered.filter(traveler => 
        traveler.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        traveler.primaryPhone.includes(searchTerm) ||
        traveler.passportNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        traveler.agentName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCampaign) {
      filtered = filtered.filter(traveler => traveler.campaignId === selectedCampaign);
    }

    if (paymentTypeFilter) {
      filtered = filtered.filter(traveler => traveler.paymentType === paymentTypeFilter);
    }

    if (paymentStatusFilter) {
      filtered = filtered.filter(traveler => {
        if (paymentStatusFilter === 'paid') {
          return traveler.remainingBalance === 0;
        } else if (paymentStatusFilter === 'partial') {
          return traveler.remainingBalance > 0 && traveler.remainingBalance < traveler.totalAmount;
        } else if (paymentStatusFilter === 'unpaid') {
          return traveler.remainingBalance === traveler.totalAmount;
        }
        return true;
      });
    }

    setFilteredTravelers(filtered);
  }, [travelers, searchTerm, selectedCampaign, paymentTypeFilter, paymentStatusFilter]);

  const handleCreateTraveler = () => {
    setEditingTraveler(null);
    setShowForm(true);
  };

  const handleEditTraveler = (traveler) => {
    setEditingTraveler(traveler);
    setShowForm(true);
  };

  const handleDeleteTraveler = (travelerId) => {
    if (window.confirm(t('confirmDelete'))) {
      setTravelers(prev => prev.map(t => 
        t.id === travelerId 
          ? { ...t, isDeleted: true, deletedAt: new Date().toISOString() }
          : t
      ));
    }
  };

  const handleViewDetails = (traveler) => {
    setSelectedTraveler(traveler);
    setShowDetails(true);
  };

  const handleFormSubmit = (travelerData) => {
    if (editingTraveler) {
      // Update existing traveler
      setTravelers(prev => prev.map(t => 
        t.id === editingTraveler.id 
          ? { ...t, ...travelerData, updatedAt: new Date().toISOString() }
          : t
      ));
    } else {
      // Create new traveler
      const newTraveler = {
        id: Date.now().toString(),
        ...travelerData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isDeleted: false
      };
      setTravelers(prev => [...prev, newTraveler]);
    }
    setShowForm(false);
    setEditingTraveler(null);
  };

  const getCampaignName = (campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    return campaign ? campaign.campaignName : t('unknownCampaign');
  };

  const getPaymentStatus = (traveler) => {
    if (traveler.remainingBalance === 0) return 'paid';
    if (traveler.remainingBalance === traveler.totalAmount) return 'unpaid';
    return 'partial';
  };

  const getTotalStats = () => {
    const totalTravelers = filteredTravelers.length;
    const totalRevenue = filteredTravelers.reduce((sum, t) => sum + t.totalAmount, 0);
    const totalPaid = filteredTravelers.reduce((sum, t) => sum + (t.totalAmount - t.remainingBalance), 0);
    const totalOutstanding = filteredTravelers.reduce((sum, t) => sum + t.remainingBalance, 0);
    const totalCommissions = filteredTravelers.reduce((sum, t) => sum + t.agentCommission, 0);

    return {
      totalTravelers,
      totalRevenue,
      totalPaid,
      totalOutstanding,
      totalCommissions
    };
  };

  const stats = getTotalStats();

  if (showForm) {
    return (
      <TravelerForm
        traveler={editingTraveler}
        campaigns={campaigns}
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setShowForm(false);
          setEditingTraveler(null);
        }}
      />
    );
  }

  if (showDetails && selectedTraveler) {
    return (
      <TravelerDetails
        traveler={selectedTraveler}
        campaign={campaigns.find(c => c.id === selectedTraveler.campaignId)}
        onBack={() => {
          setShowDetails(false);
          setSelectedTraveler(null);
        }}
        onEdit={() => {
          setShowDetails(false);
          handleEditTraveler(selectedTraveler);
        }}
      />
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <FaUsers className="text-blue-600" />
            {t('title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('subtitle')}
          </p>
        </div>
        <button
          onClick={handleCreateTraveler}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors"
        >
          <FaPlus />
          {t('addTraveler')}
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-blue-600">{stats.totalTravelers}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">{t('travelers.totalTravelers')}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-600">${stats.totalRevenue.toLocaleString()}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">{t('totalRevenue')}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-emerald-600">${stats.totalPaid.toLocaleString()}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">{t('totalPaid')}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-red-600">${stats.totalOutstanding.toLocaleString()}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">{t('totalOutstanding')}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-purple-600">${stats.totalCommissions.toLocaleString()}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">{t('totalCommissions')}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6">
        <div className="flex items-center gap-2 mb-4">
          <FaFilter className="text-gray-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">{t('filters')}</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Campaign Filter */}
          <select
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('allCampaigns')}</option>
            {campaigns.map(campaign => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.campaignName}
              </option>
            ))}
          </select>

          {/* Payment Type Filter */}
          <select
            value={paymentTypeFilter}
            onChange={(e) => setPaymentTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('allPaymentTypes')}</option>
            <option value="cash">{t('cash')}</option>
            <option value="installments">{t('installments')}</option>
          </select>

          {/* Payment Status Filter */}
          <select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('allPaymentStatuses')}</option>
            <option value="paid">{t('paid')}</option>
            <option value="partial">{t('partiallyPaid')}</option>
            <option value="unpaid">{t('unpaid')}</option>
          </select>
        </div>
      </div>

      {/* Travelers Grid */}
      {filteredTravelers.length === 0 ? (
        <div className="text-center py-12">
          <FaUsers className="mx-auto text-6xl text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-xl font-semibold text-gray-500 dark:text-gray-400 mb-2">
            {t('noTravelers')}
          </h3>
          <p className="text-gray-400 dark:text-gray-500">
            {searchTerm || selectedCampaign || paymentTypeFilter || paymentStatusFilter
              ? t('noTravelersMatchFilter')
              : t('noTravelersYet')
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTravelers.map(traveler => (
            <TravelerCard
              key={traveler.id}
              traveler={traveler}
              campaignName={getCampaignName(traveler.campaignId)}
              paymentStatus={getPaymentStatus(traveler)}
              onView={() => handleViewDetails(traveler)}
              onEdit={() => handleEditTraveler(traveler)}
              onDelete={() => handleDeleteTraveler(traveler.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TravelerManagement;